import { useState, useEffect } from 'react';
import axiosApi from '../helpers/api_helper';
import { api} from '../utils/variables';
const CACHE_KEY = 'trivium_all_filters_cache';

/**
 * Transform API response to FilterPanel format
 */
const transformFiltersData = (data) => {
  if (!data || !data.filters || !Array.isArray(data.filters)) {
    return [];
  }

  // Transform API response to FilterPanel format
  const transformedSections = data.filters.map(filter => {
    // Determine filter type: radio for "Number of Players", checkbox for others
    const isRadio = filter.value === 'number_of_players';

    return {
      id: filter.value,
      title: filter.name,
      type: isRadio ? 'radio' : 'checkbox',
      options: filter.options.map(option => ({
        key: String(option.value), // Convert to string for consistency
        label: option.name
      }))
    };
  });

  // Add sorting section if available
  if (data.sorting && Array.isArray(data.sorting) && data.sorting.length > 0) {
    // Create sort options with both ascending and descending variants
    const sortOptions = [];
    data.sorting.forEach(sort => {
      sortOptions.push(
        { key: `-${sort.value}`, label: `${sort.name} (High → Low)` },
        { key: sort.value, label: `${sort.name} (Low → High)` }
      );
    });

    transformedSections.push({
      id: 'sort',
      title: 'Sort by',
      type: 'radio',
      options: sortOptions
    });
  }

  return transformedSections;
};

/**
 * Custom hook to fetch and transform filter data from the API
 * Caches the response in localStorage to avoid repeated API calls
 * @returns {Object} { filterSections, filtersLoading, error }
 */
export const useFilters = () => {
  const [filterSections, setFilterSections] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setFiltersLoading(true);
        setError(null);

        // Check cache first
        const cachedData = localStorage.getItem(CACHE_KEY);
        if (cachedData) {
          try {
            const parsedCache = JSON.parse(cachedData);
            const transformedSections = transformFiltersData(parsedCache);
            setFilterSections(transformedSections);
            setFiltersLoading(false);
            return; // Use cached data, skip API call
          } catch (parseError) {
            console.warn('Error parsing cached filters, fetching fresh data:', parseError);
            // If cache is corrupted, continue to fetch from API
          }
        }

        // Fetch from API if no cache or cache is invalid
        const response = await axiosApi.get(`${api}/tracker/all_filters`);
        const data = response.data;

        // Store in cache
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        } catch (storageError) {
          console.warn('Failed to cache filters data:', storageError);
          // Continue even if caching fails
        }

        const transformedSections = transformFiltersData(data);
        setFilterSections(transformedSections);
      } catch (err) {
        console.error('Error fetching filters:', err);
        setError(err);

        // Try to use cached data as fallback on error
        const cachedData = localStorage.getItem(CACHE_KEY);
        if (cachedData) {
          try {
            const parsedCache = JSON.parse(cachedData);
            const transformedSections = transformFiltersData(parsedCache);
            setFilterSections(transformedSections);
          } catch (parseError) {
            setFilterSections([]);
          }
        } else {
          setFilterSections([]);
        }
      } finally {
        setFiltersLoading(false);
      }
    };

    fetchFilters();
  }, []);

  return { filterSections, filtersLoading, error };
};
